import React, { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery } from "react-query";
import { DEFAULT_PER_PAGE } from "../../../helpers/default";
import ContentBlock from "../../../components/Layout/ContentBlock";
import FilterMenu from "../../../components/Layout/FilterMenu";
import {
    ButtonSecondary,
    Pagination,
    TextInput,
    SelectInput,
} from "../../../components/globals";
import TableFilterBox from "../../../components/common/TableFilterBox";
import {
    ButtonSmall
} from "../../../components/common/Buttons";
import {
    antdAsyncMessageError,
    antdAsyncMessageSuccess,
} from "../../../helpers/AsyncMessage";
import { LockOutlined } from "@ant-design/icons";
import { Modal, DatePicker } from "antd";
import { useModal } from "../../../hooks";
import axios from "axios";
import ContentValidation from "../../../components/common/ContentValidation";
import TabUi from "./TabUi";
import { IconVVStockDocument } from "../../../components/Layout/Icons";
import { IconSearch } from "../../../components/Layout/Icons";
import DocumentDetail from "./DocumentDetail";
import ShipmentComponentIndex from "../../../components/orders/accepted_orders/items/ShipmentComponentIndex";
import ShipmentStoreSvg from "../../../../assets/images/shipment/shipment_store.svg";

const { confirm } = Modal;

const AllDocuments = ({ tabId, setTabId }) => {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [totals, setTotals] = useState(0);
    const [selectedDocumentId, setSelectedDocumentId] = useState(null);
    const [visibleShipmentId, setVisibleShipmentId] = useState(null);
    const [fullDocumentsCache, setFullDocumentsCache] = useState(new Map());
    const [shipmentVariables, setShipmentVariables] = useState({
        pack: 1,
        weight: "",
        lp_express_sizes: "Small",
        carrying: false,
        recycle: false,
        connect: false,
        cod: false,
        cod_amount: 0,
        sendingDocuments: false,
        who: "",
        deliveryTime: false,
        deliveryTimeHours: [],
        delivery_after_18: false,
        delivery_same_day: false,
        deliveryOnSaturday: false,
        order_return: false,
        swap: false,
        ignore_stock_check: false,
    });
    const [skipLabels, setSkipLabels] = useState(false);

    const handleShipmentChangeText = (eventOrPayload) => {
        if (!eventOrPayload) return;
        if (eventOrPayload.target) {
            const { name, value } = eventOrPayload.target;
            if (!name) return;
            setShipmentVariables((prev) => ({ ...prev, [name]: value }));
            return;
        }
        const { name, value } = eventOrPayload;
        if (name) {
            setShipmentVariables((prev) => ({ ...prev, [name]: value }));
        }
    };

    const WrapperShipmentForDocument = ({ doc, onClose }) => {
        const [fullDoc, setFullDoc] = React.useState(null);
        const [loading, setLoading] = React.useState(true);

        React.useEffect(() => {
            const fetchFullDocument = async () => {
                try {
                    setLoading(true);

                    if (fullDocumentsCache.has(doc.id)) {
                        setFullDoc(fullDocumentsCache.get(doc.id));
                        setLoading(false);
                        return;
                    }

                    const response = await axios.get(`/api/v1/product_transfers/${doc.id}`);
                    const fullData = response.data;

                    setFullDocumentsCache(prev => new Map(prev).set(doc.id, fullData));
                    setFullDoc(fullData);
                } catch (error) {
                    console.error('Error fetching full document:', error);
                    setFullDoc(doc);
                } finally {
                    setLoading(false);
                }
            };

            fetchFullDocument();
        }, [doc.id]);

        if (loading || !fullDoc) {
            return <div>Loading...</div>;
        }

        const orderRows = fullDoc?.order_rows?.length > 0
            ? fullDoc.order_rows.map(row => {
                const transferItem = fullDoc.product_transfer_items?.find(
                    item => item.id === row.id_order_row
                );
                return {
                    ...row,
                    selected: true,
                    product_name: row.product_name || transferItem?.product?.title || 'Unknown Product',
                    product_reference: row.product_reference || transferItem?.product?.sku || '',
                    quantity: row.quantity || transferItem?.quantity || 1,
                    unit_price_tax_incl: row.unit_price_tax_incl || transferItem?.prime_cost || 0,
                    shipment_order_rows: row.shipment_order_rows || [],
                    confirmed_at: transferItem?.confirmed_at,
                };
            })
            : (fullDoc?.product_transfer_items || []).map((item) => ({
                id: item.id,
                order_id: fullDoc?.id,
                selected: true,
                quantity: item.quantity,
                product_name: item.product?.title || "Unknown Product",
                product_reference: item.product?.sku || "",
                unit_price_tax_incl: item.prime_cost || 0,
                shipment_order_rows: [],
                confirmed_at: item.confirmed_at,
            }));

        const confirmedOrderRows = orderRows.filter(row => row.confirmed_at);

        // Check if there's a successful VV transport shipment
        const successfulShipments = (fullDoc?.shipments || []).filter(shipment =>
            shipment.tracking_numbers?.length > 0 ||
            (shipment.shipment_type?.toLowerCase() === 'vv_transport' && !shipment.api_errors)
        );
        const hasSuccessfulVVTransport = successfulShipments.some(s =>
            s.shipment_type?.toLowerCase() === 'vv_transport'
        );

        const shippedQuantities = {};
        successfulShipments.forEach(shipment => {
            (shipment.shipment_order_rows || []).forEach(sor => {
                const rowId = sor.order_row_id || sor.order_row?.id;
                if (rowId) {
                    shippedQuantities[rowId] = (shippedQuantities[rowId] || 0) + (sor.quantity || 0);
                }
            });
        });

        // If VV transport successful but no shipment_order_rows tracked, mark all confirmed as shipped
        const hasAnyShippedQty = Object.keys(shippedQuantities).length > 0;

        let unshippedOrderRows;
        if (hasSuccessfulVVTransport && !hasAnyShippedQty) {
            // All confirmed items are considered shipped via VV Transport
            unshippedOrderRows = [];
        } else {
            unshippedOrderRows = confirmedOrderRows.map(row => {
                const shippedQty = shippedQuantities[row.id] || 0;
                const remainingQty = row.quantity - shippedQty;
                return {
                    ...row,
                    quantity: remainingQty,
                    original_quantity: row.quantity,
                    shipped_quantity: shippedQty,
                };
            }).filter(row => row.quantity > 0);
        }

        const fakeOrderRows = unshippedOrderRows.length > 0 ? unshippedOrderRows : [];

        if (fakeOrderRows.length === 0) {
            return (
                <div className="p-3 text-center">
                    <div className="text-success mb-2">✓ All confirmed products have been shipped</div>
                    <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
                </div>
            );
        }


        const shipping_address = {
            firstname: fullDoc?.receiver?.name || fullDoc?.receiver?.title || "Receiver",
            lastname: "",
            postal_code: fullDoc?.receiver?.postal_code || "00000",
            street: fullDoc?.receiver?.address || "Address not provided",
            city: fullDoc?.receiver?.city || "Vilnius",
            phone: fullDoc?.receiver?.phone || "+37000000000",
            other: fullDoc?.receiver?.email || "",
            comment: fullDoc?.receiver?.comment || "",
            country_id: fullDoc?.receiver?.country_id || fullDoc?.country_id || 122,
            country: fullDoc?.receiver?.country || fullDoc?.country || "LT",
            parcel_terminal_id: null,
            name: fullDoc?.receiver?.name || fullDoc?.receiver?.title || "Receiver",
            address: fullDoc?.receiver?.address || "Address not provided",
            email: fullDoc?.receiver?.email || "",
        };
        const carrier = {
            module_name: fullDoc?.delivery || "dpd",
            name: fullDoc?.delivery || "dpd",
            pickup_address_id: fullDoc?.sender?.pickup_address?.id || null
        };

        const handleShipmentCreated = async (orderId, responseData) => {
            onClose();

            await new Promise(resolve => setTimeout(resolve, 1500));

            setFullDocumentsCache(prev => {
                const newCache = new Map(prev);
                newCache.delete(doc.id);
                return newCache;
            });

            await refetchDocuments();
        };

        return (
            <ShipmentComponentIndex
                order_rows={fakeOrderRows}
                id={fullDoc?.id}
                id_order={fullDoc?.id}
                remark={""}
                shipping_address={shipping_address}
                total_price={0}
                payment_module={"cash"}
                carrier={carrier}
                pickup_address_id={fullDoc?.sender?.pickup_address?.id || null}
                onClose={onClose}
                order_invoices={[]}
                order_cart_rules={[]}
                billing_address={{}}
                shipments={(fullDoc?.shipments || []).map(shipment => ({
                    ...shipment,
                    courier_trackings: shipment.courier_trackings || [],
                    shipment_additional_services: shipment.shipment_additional_services || [],
                    shipment_order_rows: (shipment.shipment_order_rows || []).map(row => ({
                        ...row,
                        order_row: row.order_row || {},
                        order_row_id: row.order_row_id || row.order_row?.id,
                    })),
                    shipment_emails: shipment.shipment_emails || [],
                    pickup_address: shipment.pickup_address || {
                        company: '',
                        address: '',
                        city: '',
                        postal_code: '',
                        contact_person: '',
                        phone: '',
                        email: '',
                        code: '',
                        pickup_time_from: '',
                        work_time_to: '',
                    },
                    shipment_address: shipment.shipment_address || {
                        parcel_terminal_id: null,
                        address: '',
                        city: '',
                        postal_code: '',
                        name: '',
                        company: null,
                        email: '',
                        phone: '',
                        comment: '',
                        country: null,
                    },
                }))}
                assigned_bills={[]}
                cod={false}
                leasing_cod={false}
                leasing_advance={0}
                waiting_payments={[]}
                order_shop_id={0}
                order_shop={{ identifier: "VV" }}
                form_type={"ProductTransfer"}
                total_shipping_tax_incl={0}
                callBackOnCreate={handleShipmentCreated}
            />
        );
    };

    const handleOpenShipment = (rowId) => {
        if (visibleShipmentId === rowId) {
            setVisibleShipmentId(null);
            return;
        }
        setVisibleShipmentId(rowId);
        const anchor = document.querySelector(`.shipmentIndex_${rowId}`);
        if (anchor) {
            anchor.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    };

    const handleKeyDownOpenShipment = (event, rowId) => {
        if (!event) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleOpenShipment(rowId);
        }
    };

    const [filterDocumentDate, setFilterDocumentDate] = useState(null);
    const [filterProcessingDate, setFilterProcessingDate] = useState(null);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterSender, setFilterSender] = useState("");
    const [filterReceiver, setFilterReceiver] = useState("");
    const [filterManager, setFilterManager] = useState("");
    const [filterDelivery, setFilterDelivery] = useState("");
    const [filterVvSku, setFilterVvSku] = useState("");
    const [filterBbSku, setFilterBbSku] = useState("");
    const [filterDocumentNumber, setFilterDocumentNumber] = useState("");
    const [filterSpecialTransfer, setFilterSpecialTransfer] = useState("");

    const { modalOpt, setModalOpt, closeModal } = useModal();

    const fetchDocuments = async () => {
        let url = `/api/v1/product_transfers?page=${page}&per_page=${perPage}`;

        const queryParams = {};

        if (filterDocumentDate) {
            const dateStr = filterDocumentDate.format('YYYY-MM-DD');
            queryParams['q[created_at_gteq]'] = `${dateStr} 00:00:00`;
            queryParams['q[created_at_lteq]'] = `${dateStr} 23:59:59`;
        }
        if (filterProcessingDate) {
            const dateStr = filterProcessingDate.format('YYYY-MM-DD');
            queryParams['q[updated_at_gteq]'] = `${dateStr} 00:00:00`;
            queryParams['q[updated_at_lteq]'] = `${dateStr} 23:59:59`;
        }
        if (filterStatus) {
            queryParams['q[status_eq]'] = filterStatus;
        }
        if (filterSender) {
            queryParams['q[sender_name_or_sender_title_cont]'] = filterSender;
        }
        if (filterReceiver) {
            queryParams['q[receiver_name_or_receiver_title_cont]'] = filterReceiver;
        }
        if (filterManager) {
            queryParams['q[manager_full_name_cont]'] = filterManager;
        }
        if (filterDelivery) {
            queryParams['q[delivery_cont]'] = filterDelivery;
        }
        if (filterVvSku) {
            queryParams['q[product_transfer_items_product_vv_sku_cont]'] = filterVvSku;
        }
        if (filterBbSku) {
            queryParams['q[product_transfer_items_product_identifier_cont]'] = filterBbSku;
        }
        if (filterDocumentNumber) {
            queryParams['q[document_number_cont]'] = filterDocumentNumber;
        }
        if (filterSpecialTransfer !== "") {
            queryParams['q[special_transfer_eq]'] = filterSpecialTransfer;
        }

        const queryString = new URLSearchParams(queryParams).toString();
        if (queryString) {
            url += `&${queryString}`;
        }

        try {
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching documents:', error);
            return {
                data: [],
                total: 0,
                totals: 0
            };
        }
    };

    const {
        data: documents,
        isFetching: documentsLoading,
        error: documentsError,
        refetch: refetchDocuments,
    } = useQuery(["vv_stock_all_documents", page, perPage], fetchDocuments, {
        enabled: false
    });

    const handleFilterChange = () => {
        refetchDocuments();
    };

    React.useEffect(() => {
        refetchDocuments();
    }, [page, perPage]);

    const handleFilterKeyDown = (e) => {
        if (e.key === 'Enter') handleFilterChange();
    };

    const handleFilterClear = () => {
        setFilterDocumentDate(null);
        setFilterProcessingDate(null);
        setFilterStatus("");
        setFilterSender("");
        setFilterReceiver("");
        setFilterManager("");
        setFilterDelivery("");
        setFilterVvSku("");
        setFilterBbSku("");
        setFilterDocumentNumber("");
        setFilterSpecialTransfer("");
    };

    React.useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (e.key === 'Enter' && !e.target.closest('.ant-select-dropdown')) {
                handleFilterChange();
            }
        };
        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, []);

    const deleteDocumentMutation = useMutation(
        (id) => axios.delete(`/api/v1/product_transfers/${id}`),
        {
            onSuccess: () => {
                antdAsyncMessageSuccess({
                    text: "Successfully deleted",
                    key: "delete_document",
                });
                refetchDocuments();
            },
            onError: () => {
                antdAsyncMessageError({
                    text: "Error occurred",
                    key: "delete_document",
                });
            },
        }
    );

    React.useEffect(() => {
        if (documents?.totals) {
            setTotals(documents?.totals);
        }
    }, [documents]);

    React.useEffect(() => {
        refetchDocuments();
    }, [modalOpt]);

    const handleBackToList = useCallback(() => {
        setSelectedDocumentId(null);
    }, []);

    useEffect(() => {
        const onPopState = (event) => {
            if (selectedDocumentId && (!event.state || !event.state.documentDetailId)) {
                setSelectedDocumentId(null);
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [selectedDocumentId]);

    const openDocumentDetail = useCallback((id) => {
        window.history.pushState({ documentDetailId: id }, '');
        setSelectedDocumentId(id);
    }, []);

    const goBackFromDetail = useCallback(() => {
        window.history.back();
    }, []);

    if (selectedDocumentId) {
        return (
            <DocumentDetail
                documentId={selectedDocumentId}
                onBack={goBackFromDetail}
                onRefresh={() => refetchDocuments()}
            />
        );
    }

    return (
        <>
            <ContentBlock>
                <FilterMenu
                    length={documents?.data?.length}
                    text2={`products`}
                    iconComponent={<IconVVStockDocument />}
                    onChangeCountPage={setPerPage}
                    countPage={perPage}
                    page={page}
                    totals={documents?.totals}
                />
            </ContentBlock>

            <div className="col-12">
                <TabUi tabId={tabId} setTabId={setTabId} />
            </div>

            <ContentBlock>
                <TableFilterBox
                    filter={true}
                    onFilter={() => {
                        handleFilterChange();
                    }}
                    add={false}
                    clearFilter={() => handleFilterClear()}
                >
                    <div className="d-flex justify-content-between flex-1 flex-wrap">
                        <div className="d-flex flex-wrap">
                            <div className="px-1">
                                <TextInput
                                    text="Document No."
                                    name="document_number"
                                    value={filterDocumentNumber}
                                    onChange={(val) => {
                                        setFilterDocumentNumber(val.target.value);
                                    }}
                                    onKeyDown={handleFilterKeyDown}
                                />
                            </div>
                            <div className="px-1">
                                <div className="form-group">
                                    <label className="text-sm-label my-1">Document date</label>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={filterDocumentDate}
                                        onChange={(date) => {
                                            setFilterDocumentDate(date);
                                        }}
                                        format="YYYY-MM-DD"
                                        allowClear
                                        placeholder="Select date"
                                    />
                                </div>
                            </div>
                            <div className="px-1">
                                <div className="form-group">
                                    <label className="text-sm-label my-1">Processing date</label>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={filterProcessingDate}
                                        onChange={(date) => {
                                            setFilterProcessingDate(date);
                                        }}
                                        format="YYYY-MM-DD"
                                        allowClear
                                        placeholder="Select date"
                                    />
                                </div>
                            </div>
                            <div className="px-1">
                                <SelectInput
                                    text="Status"
                                    name="status"
                                    label="Status"
                                    value_return="light"
                                    options={[
                                        { label: "All", value: "" },
                                        { label: "New", value: "new" },
                                        { label: "Completed", value: "completed" },
                                        { label: "Canceled", value: "canceled" },
                                    ]}
                                    value={filterStatus}
                                    onChange={(val) => {
                                        setFilterStatus(val.target.value);
                                    }}
                                />
                            </div>
                            <div className="px-1">
                                <SelectInput
                                    text="Special Transfer"
                                    name="special_transfer"
                                    label="Special Transfer"
                                    value_return="light"
                                    options={[
                                        { label: "All", value: "" },
                                        { label: "Yes", value: "true" },
                                        { label: "No", value: "false" },
                                    ]}
                                    value={filterSpecialTransfer}
                                    onChange={(val) => {
                                        setFilterSpecialTransfer(val.target.value);
                                    }}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="Sender"
                                    name="sender"
                                    value={filterSender}
                                    onChange={(val) => {
                                        setFilterSender(val.target.value);
                                    }}
                                    onKeyDown={handleFilterKeyDown}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="Receiver"
                                    name="receiver"
                                    value={filterReceiver}
                                    onChange={(val) => {
                                        setFilterReceiver(val.target.value);
                                    }}
                                    onKeyDown={handleFilterKeyDown}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="Manager"
                                    name="manager"
                                    value={filterManager}
                                    onChange={(val) => {
                                        setFilterManager(val.target.value);
                                    }}
                                    onKeyDown={handleFilterKeyDown}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="Delivery"
                                    name="delivery"
                                    value={filterDelivery}
                                    onChange={(val) => {
                                        setFilterDelivery(val.target.value);
                                    }}
                                    onKeyDown={handleFilterKeyDown}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="VV SKU"
                                    name="vv_sku"
                                    value={filterVvSku}
                                    onChange={(val) => {
                                        setFilterVvSku(val.target.value);
                                    }}
                                    onKeyDown={handleFilterKeyDown}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="BB SKU"
                                    name="bb_sku"
                                    value={filterBbSku}
                                    onChange={(val) => {
                                        setFilterBbSku(val.target.value);
                                    }}
                                    onKeyDown={handleFilterKeyDown}
                                />
                            </div>
                        </div>
                    </div>
                </TableFilterBox>

                <div>
                    <div className="table-responsive" style={{ overflowX: "auto" }}>
                        <ContentValidation
                            loader={documentsLoading}
                            array={documents?.data}
                            error={documentsError}
                        >
                            <table className="table mt-3">
                                <thead>
                                    <tr className="text-muted">
                                        <th className="text-black">Document No.</th>
                                        <th className="text-black">Creation Date</th>
                                        <th className="text-black">Status</th>
                                        <th className="text-black">Special Delivery</th>
                                        <th className="text-black">Supplier Code</th>
                                        <th className="text-black">Manager</th>
                                        <th className="text-black">Sup. Order No.</th>
                                        <th className="text-black">Sender</th>
                                        <th className="text-black">Receiver</th>
                                        <th className="text-black">Arrival Date</th>
                                        <th className="text-black">Delivery</th>
                                        <th className="text-black">Shipment</th>
                                        <th className="text-black">Processing Dates</th>
                                        <th className="text-black">Product QTY</th>
                                        <th className="text-black">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents?.data?.map?.((item) => {
                                        const totalProducts = item?.product_transfer_items?.length || 0;

                                        const confirmedItems = (item?.product_transfer_items || []).filter(ti => ti.confirmed_at);
                                        const confirmedCount = confirmedItems.length;

                                        const totalConfirmedQty = confirmedItems.reduce((sum, ti) => sum + (ti.quantity || 0), 0);

                                        // Successful shipments: has tracking numbers OR is VV transport without errors
                                        const successfulShipments = (item?.shipments || []).filter(s =>
                                            s.tracking_numbers?.length > 0 ||
                                            (s.shipment_type?.toLowerCase() === 'vv_transport' && !s.api_errors)
                                        );

                                        // Check if there's a successful VV transport shipment
                                        const hasSuccessfulVVTransport = successfulShipments.some(s =>
                                            s.shipment_type?.toLowerCase() === 'vv_transport'
                                        );

                                        // Calculate shipped qty - for VV transport without shipment_order_rows, count all confirmed as shipped
                                        let totalShippedQty = successfulShipments.reduce((shipmentSum, shipment) => {
                                            const rowsQty = (shipment.shipment_order_rows || []).reduce((rowSum, sor) => {
                                                return rowSum + (sor.quantity || 0);
                                            }, 0);
                                            return shipmentSum + rowsQty;
                                        }, 0);

                                        // If VV transport successful but no shipment_order_rows tracked, assume all confirmed items shipped
                                        if (hasSuccessfulVVTransport && totalShippedQty === 0 && totalConfirmedQty > 0) {
                                            totalShippedQty = totalConfirmedQty;
                                        }

                                        const hasUnshippedProducts = totalConfirmedQty > totalShippedQty;

                                        const latestShipment = item?.shipments?.[item.shipments.length - 1];
                                        const latestError = latestShipment?.api_errors;

                                        return (
                                            <tr className="align-middle tableColumn" key={item.id}>
                                                <td className="align-middle">
                                                    <div className="d-flex align-items-center">
                                                        <span>{item?.document_number}</span>
                                                        {item?.locked_at && (
                                                            <LockOutlined
                                                                style={{ marginLeft: 8, color: "#faad14" }}
                                                                title="Document is locked"
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="align-middle">
                                                    {item?.created_at ? new Date(item.created_at).toLocaleString('lt-LT', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }).replace(',', '') : '-'}
                                                </td>
                                                <td className="align-middle">
                                                    <div>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`${item?.status === "new" ? "text-primary" :
                                                                item?.status === "pending" ? "text-warning" :
                                                                    item?.status === "completed" ? "text-success" :
                                                                        item?.status === "canceled" ? "text-danger" :
                                                                            item?.status === "failed" ? "text-danger" :
                                                                                "text-secondary"
                                                                } mr-2`} style={{ fontSize: "14px" }}>
                                                                ●
                                                            </span>
                                                            {item?.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : ''}
                                                        </div>
                                                        {item?.status === "failed" && item?.error && (
                                                            <div className="text-danger" style={{ fontSize: "11px", marginTop: "4px", marginLeft: "18px" }}>
                                                                {item.error}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="align-middle">
                                                    {item?.special_transfer ? (
                                                        <span className="text-success" style={{ fontWeight: 600 }}>Yes</span>
                                                    ) : (
                                                        <span className="text-muted">No</span>
                                                    )}
                                                </td>
                                                <td className="align-middle">{item?.supplier_code}</td>
                                                <td className="align-middle">{item?.manager}</td>
                                                <td className="align-middle">{item?.sup_order_no}</td>
                                                <td className="align-middle">{item?.sender?.title || item?.sender?.code || item?.sender?.name || '-'}</td>
                                                <td className="align-middle">{item?.receiver?.title || item?.receiver?.code || item?.receiver?.name || '-'}</td>
                                                <td className="align-middle">{item?.arrival_date}</td>
                                                <td className="align-middle">{item?.delivery}</td>
                                                <td className="align-middle">
                                                    {item?.status !== "canceled" && (
                                                        <>
                                                            <div className={`shipmentIndex_${item.id}`}></div>

                                                            <div style={{ fontSize: "13px", marginBottom: "6px", fontWeight: "500" }}>
                                                                <div style={{ color: confirmedCount === totalProducts ? "#28a745" : "#6c757d" }}>
                                                                    Confirmed: {confirmedCount}/{totalProducts}
                                                                </div>
                                                                <div style={{ color: totalShippedQty >= totalConfirmedQty && totalConfirmedQty > 0 ? "#28a745" : "#6c757d" }}>
                                                                    Shipped: {totalShippedQty}/{totalConfirmedQty}
                                                                </div>
                                                            </div>

                                                            {latestError && (
                                                                <div className="text-danger mb-1" style={{ fontSize: "10px" }}>
                                                                    ⚠ {latestError}
                                                                </div>
                                                            )}

                                                            {/* Show VV transport success */}
                                                            {latestShipment && latestShipment.shipment_type?.toLowerCase() === 'vv_transport' && !latestError && (
                                                                <div className="text-success mb-1" style={{ fontSize: "11px" }}>
                                                                    Transported with VV Transport.
                                                                </div>
                                                            )}

                                                            {confirmedCount > 0 && hasUnshippedProducts && (item?.inner_document_number || item?.sender?.code?.startsWith?.('rivile_')) && (
                                                                <>
                                                                    {visibleShipmentId === item.id ? (
                                                                        <div>
                                                                            <WrapperShipmentForDocument doc={item} onClose={() => setVisibleShipmentId(null)} />
                                                                        </div>
                                                                    ) : (
                                                                        <div
                                                                            className="btn-primary d-flex justify-content-center align-items-center cursor-pointer"
                                                                            style={{ width: 30, height: 30, borderRadius: "50%" }}
                                                                            onClick={() => handleOpenShipment(item.id)}
                                                                            aria-label="Open shipment services"
                                                                            role="button"
                                                                            tabIndex={0}
                                                                            onKeyDown={(e) => handleKeyDownOpenShipment(e, item.id)}
                                                                        >
                                                                            <img src={ShipmentStoreSvg} style={{ width: 18, height: 18, marginLeft: 2 }} />
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                                <td className="align-middle">
                                                    {item?.updated_at ? new Date(item.updated_at).toLocaleString('lt-LT', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }).replace(',', '') : '-'}
                                                </td>
                                                <td className="align-middle">{item?.product_qty}</td>
                                                <td className="align-middle">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <ButtonSmall
                                                            className="d-flex justify-content-center align-items-center"
                                                            onClick={() => {
                                                                openDocumentDetail(item.id);
                                                            }}
                                                        >
                                                            <IconSearch color="#fff" />
                                                        </ButtonSmall>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </ContentValidation>
                        <Pagination
                            setPagination={setPage}
                            page={page}
                            totals={totals}
                            pageCount={perPage}
                        />
                    </div>
                </div>
            </ContentBlock>
        </>
    );
};

export default AllDocuments; 